import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { createPortal } from 'react-dom';

/**
 * ProfileCropModal - Zuschneide-Tool für Profilbilder (1:1 quadratisch)
 *
 * Zeigt einen Crop-Dialog nach Auswahl eines Profilbildes.
 * Der User kann das Bild verschieben und zoomen, um den
 * gewünschten Ausschnitt im quadratischen Rahmen zu positionieren.
 */
export default function ProfileCropModal({ imageSrc, onSave, onCancel }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [blobSrc, setBlobSrc] = useState(null);

  // Bild als Blob laden um CORS-Probleme zu umgehen
  React.useEffect(() => {
    if (!imageSrc) return;
    if (imageSrc.startsWith('data:') || imageSrc.startsWith('blob:')) {
      setBlobSrc(imageSrc);
      return;
    }
    let revoke = null;
    fetch(imageSrc)
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        revoke = url;
        setBlobSrc(url);
      })
      .catch(() => setBlobSrc(imageSrc)); // Fallback auf Original
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [imageSrc]);

  const onCropChange = useCallback((c) => setCrop(c), []);
  const onZoomChange = useCallback((z) => setZoom(z), []);
  const onCropCompleteInternal = useCallback((_, pixels) => setCroppedAreaPixels(pixels), []);

  const handleSave = async () => {
    if (!croppedAreaPixels) return;
    setIsProcessing(true);

    try {
      // Bild aus der bereits geladenen Blob-URL verwenden (CORS-sicher)
      const image = new Image();
      await new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = () => reject(new Error('Bild konnte nicht geladen werden'));
        image.src = blobSrc || imageSrc;
      });

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Profilbild: 512x512 max
      const size = Math.min(512, croppedAreaPixels.width);
      canvas.width = size;
      canvas.height = size;

      ctx.drawImage(
        image,
        croppedAreaPixels.x, croppedAreaPixels.y,
        croppedAreaPixels.width, croppedAreaPixels.height,
        0, 0, size, size
      );

      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error('Blob-Erstellung fehlgeschlagen'))),
          'image/jpeg',
          0.92
        );
      });

      onSave(blob);
    } catch (error) {
      console.error('Crop error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100001] bg-black flex flex-col"
      style={{ touchAction: 'none' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-black/60 backdrop-blur-sm z-10">
        <button
          onClick={onCancel}
          className="text-white/50 hover:text-white text-sm font-light tracking-wide transition-colors"
        >
          Abbrechen
        </button>
        <span className="text-white/70 text-xs font-light tracking-wider uppercase">
          Profilbild zuschneiden
        </span>
        <button
          onClick={handleSave}
          disabled={isProcessing}
          className="text-[#10B981] hover:text-[#34D399] text-sm font-normal tracking-wide disabled:opacity-40 transition-colors"
        >
          {isProcessing ? (
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-3 border-2 border-[#10B981]/40 border-t-[#10B981] rounded-full animate-spin" />
              Speichern...
            </span>
          ) : (
            'Speichern'
          )}
        </button>
      </div>

      {/* Crop Area — 1:1 quadratisch */}
      <div className="flex-1 relative">
        {!blobSrc ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
          </div>
        ) : (
        <Cropper
          image={blobSrc}
          crop={crop}
          zoom={zoom}
          aspect={1}
          onCropChange={onCropChange}
          onZoomChange={onZoomChange}
          onCropComplete={onCropCompleteInternal}
          cropShape="rect"
          showGrid={false}
          style={{
            containerStyle: { background: '#000' },
            cropAreaStyle: {
              border: '2px solid rgba(16, 185, 129, 0.5)',
              borderRadius: '16px',
              boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.75)',
            },
          }}
        />
        )}
      </div>

      {/* Zoom Slider */}
      <div className="px-6 py-4 bg-black/60 backdrop-blur-sm">
        <div className="flex items-center gap-3 max-w-sm mx-auto">
          <svg className="w-3.5 h-3.5 text-white/30 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
          </svg>
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1 h-[2px] bg-white/10 rounded-full appearance-none cursor-pointer
                       [&::-webkit-slider-thumb]:appearance-none
                       [&::-webkit-slider-thumb]:w-5
                       [&::-webkit-slider-thumb]:h-5
                       [&::-webkit-slider-thumb]:rounded-full
                       [&::-webkit-slider-thumb]:bg-[#10B981]
                       [&::-webkit-slider-thumb]:shadow-lg
                       [&::-webkit-slider-thumb]:shadow-[#10B981]/30
                       [&::-webkit-slider-thumb]:cursor-pointer
                       [&::-moz-range-thumb]:w-5
                       [&::-moz-range-thumb]:h-5
                       [&::-moz-range-thumb]:rounded-full
                       [&::-moz-range-thumb]:bg-[#10B981]
                       [&::-moz-range-thumb]:border-0
                       [&::-moz-range-thumb]:cursor-pointer"
          />
          <svg className="w-4 h-4 text-white/30 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
          </svg>
        </div>
        <p className="text-center text-white/20 text-[10px] mt-2 tracking-wider uppercase">
          Bild verschieben &amp; zoomen
        </p>
      </div>
    </div>,
    document.body
  );
}
